import { Fragment } from 'react';
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../../lib/messages';
import type { TargetingClient } from '../../../../../targeting/api';
import { createCustomTargetingExtRule } from '../../../../../targeting/customTargetingExtModel';
import type { MadBuyLineItemGeneralForm } from '../../../lineItemsModel';
import { CustomTargetingExtRule } from './CustomTargetingExtRule';
import { LogicalOperatorRadio } from './LogicalOperatorRadio';
import { customTargetingGroupPath } from './paths';

export function CustomTargetingExtGroup({
  client,
  groupIndex,
  onRemove,
}: {
  client: TargetingClient;
  groupIndex: number;
  onRemove: () => void;
}) {
  'use no memo';

  const { control } = useFormContext<MadBuyLineItemGeneralForm>();
  const groupPath = customTargetingGroupPath(groupIndex);
  const rules = useFieldArray({ control, name: `${groupPath}.rules` });
  const groupOperator = useWatch({ control, name: `${groupPath}.op` });
  const operatorFieldLabel = dashboardT('madBuy.lineItems.customTargetingRuleOperator');

  return (
    <section className="border-input grid gap-4 rounded-md border p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="grid gap-2">
          <h3 className="text-sm font-semibold">
            {dashboardT('madBuy.lineItems.customTargetingGroup', { number: groupIndex + 1 })}
          </h3>
          <Controller
            control={control}
            name={`${groupPath}.op`}
            render={({ field }) => (
              <LogicalOperatorRadio
                value={field.value}
                onChange={field.onChange}
                ariaLabel={operatorFieldLabel}
              />
            )}
          />
        </div>
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={dashboardT('madBuy.lineItems.customTargetingRemoveGroup')}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
      {rules.fields.map((rule, ruleIndex) => (
        <Fragment key={rule.id}>
          {ruleIndex > 0 ? (
            <div className="text-muted-foreground text-xs font-semibold uppercase">
              {groupOperator}
            </div>
          ) : null}
          <CustomTargetingExtRule
            client={client}
            groupIndex={groupIndex}
            ruleIndex={ruleIndex}
            canRemove={rules.fields.length > 1}
            onRemove={() => {
              rules.remove(ruleIndex);
            }}
          />
        </Fragment>
      ))}
      <Button
        type="button"
        variant="ghost"
        className="w-fit"
        onClick={() => {
          rules.append(createCustomTargetingExtRule());
        }}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {dashboardT('madBuy.lineItems.customTargetingAddRule')}
      </Button>
    </section>
  );
}
